import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip,
} from "chart.js";
import { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { BsGraphDownArrow, BsGraphUpArrow } from "react-icons/bs";
import { FaBowlRice, FaMagnifyingGlass, FaRegPaste } from "react-icons/fa6";
import { GiBull, GiCorn, GiCottonFlower, GiFallingLeaf, GiFarmTractor } from "react-icons/gi";
import { GoAlertFill } from "react-icons/go";
import { GrMoney } from "react-icons/gr";
import { IoMdPerson } from "react-icons/io";
import { IoPricetagsOutline } from "react-icons/io5";
import { MdBalance, MdOutlineWavingHand } from "react-icons/md";
import { PiCowLight, PiGrainsLight, PiPlantLight } from "react-icons/pi";
import { TiWeatherPartlySunny } from "react-icons/ti";
import { VscGraph } from "react-icons/vsc";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

ChartJS.register(ArcElement, Tooltip, Legend);
const API_BASE = process.env.REACT_APP_API_URL;

const apiFetch = async (url, token) => {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.status === 401) {
    localStorage.removeItem("token");
    window.location.href = "/";
    throw new Error("Não autorizado");
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || `Erro ${response.status}`);
  }

  return data;
};

const fetchCattlePrice = async (token) => {
  try {
    return await apiFetch(`${API_BASE}/mercado/boi-gordo/`, token);
  } catch (error) {
    console.error("Erro ao buscar preço do boi:", error.message);
    return { price: 0 };
  }
};

const fetchVacaPrice = async (token) => {
  try {
    return await apiFetch(`${API_BASE}/mercado/vaca-gorda/`, token);
  } catch (error) {
    console.error("Erro ao buscar preço da vaca:", error.message);
    return { price: 0 };
  }
};

const fetchAnimais = async (token) => {
  try {
    const data = await apiFetch(`${API_BASE}/pecuaria/animal`, token);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.animais)) return data.animais;
    if (Array.isArray(data.results)) return data.results;
    return [];
  } catch (error) {
    console.error("Erro ao buscar animais:", error.message);
    return [];
  }
};

const fetchUltimasAtividades = async (token) => {
  try {
    const data = await apiFetch(`${API_BASE}/agricultura/atividades/?limite=5`, token);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.atividades)) return data.atividades;
    if (Array.isArray(data.results)) return data.results;
    return [];
  } catch (error) {
    console.error("Erro ao buscar atividades:", error.message);
    return [];
  }
};

const fetchContasReceberVencidas = async (token) => {
  try {
    const data = await apiFetch(`${API_BASE}/financeiro/contas-a-receber/?status=atrasada`, token);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.contas)) return data.contas;
    if (Array.isArray(data.results)) return data.results;
    return [];
  } catch (error) {
    console.error("Erro ao buscar contas a receber:", error.message);
    return [];
  }
};

const fetchContasPagarVencidas = async (token) => {
  try {
    const data = await apiFetch(`${API_BASE}/financeiro/contas-a-pagar/?status=atrasada`, token);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.contas)) return data.contas;
    if (Array.isArray(data.results)) return data.results;
    return [];
  } catch (error) {
    console.error("Erro ao buscar contas a pagar:", error.message);
    return [];
  }
};

const fetchContasReceberPendentes = async (token) => {
  try {
    const data = await apiFetch(`${API_BASE}/financeiro/contas-a-receber/?status=pendente`, token);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.contas)) return data.contas;
    if (Array.isArray(data.results)) return data.results;
    return [];
  } catch (error) {
    console.error("Erro ao buscar contas a receber pendentes:", error.message);
    return [];
  }
};

const fetchContasPagarPendentes = async (token) => {
  try {
    const data = await apiFetch(`${API_BASE}/financeiro/contas-a-pagar/?status=pendente`, token);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.contas)) return data.contas;
    if (Array.isArray(data.results)) return data.results;
    return [];
  } catch (error) {
    console.error("Erro ao buscar contas a pagar pendentes:", error.message);
    return [];
  }
};

const fetchWeather = async (lat, lon, token, setWeather, setLocation) => {
  try {
    const data = await apiFetch(`${API_BASE}/dashboard/clima/?lat=${lat}&lon=${lon}`, token);
    setWeather({
      temp: `${data.temp}°C`,
      max: `${data.max}°C`,
      min: `${data.min}°C`,
      description: data.description,
      humidity: data.humidity,
      wind: data.wind,
      icon: data.icon,
    });
    setLocation(`${data.city}, ${data.country}`);
  } catch (error) {
    console.error("Erro clima:", error.message);
    setLocation("Indisponível");
  }
};

function saudacaoPorHorario() {
  const hora = new Date().getHours();
  if (hora >= 5 && hora < 12) return "Bom dia";
  if (hora >= 12 && hora < 19) return "Boa tarde";
  if (hora >= 19) return "Boa noite";
  return "Boa madrugada";
}

const fetchCotacoesGraos = async (token) => {
  try {
    return await apiFetch(`${API_BASE}/dashboard/cotacoes/`, token);
  } catch (error) {
    console.error("Erro ao buscar cotações:", error.message);
    return {
      soja: { media: "—", cidades: [] },
      milho: { media: "—", cidades: [] },
      algodao: { media: "—", cidades: [] },
      arroz: { media: "—", cidades: [] },
    };
  }
};

function calcularCategoria(animal) {
  if (animal?.especie !== 1) return null;
  const meses = animal?.idade_meses;
  if (meses == null) return null;
  if (meses < 9) return "bezerro";
  if (meses < 24) return "novilhos";
  if (meses < 36) return "novilho_jovem";
  return animal.sexo === "F" ? "vaca" : "boi_adulto";
}

function AtividadeModal({ atividade, onClose }) {
  if (!atividade) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FaRegPaste size={18} /> Detalhes da Atividade
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-xs text-blue-600 uppercase font-semibold">Tipo de Atividade</p>
            <p className="text-lg font-bold text-gray-800">{atividade.tipo_atividade?.tipo_principal || "—"}</p>
            <p className="text-sm text-gray-600 mt-1">{atividade.tipo_atividade?.nome || "—"}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase font-semibold">Data</p>
              <p className="text-base font-medium text-gray-800">
                {atividade?.data ? atividade.data.split("-").reverse().join("/") : "—"}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase font-semibold">Status</p>
              <p className={`text-base font-medium ${atividade.status === "Concluído" ? "text-green-600" : "text-yellow-600"}`}>
                {atividade.status || "—"}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase font-semibold">Responsável</p>
              <p className="text-base font-medium text-gray-800">{atividade.funcionario?.nome || "—"}</p>
              <p className="text-xs text-gray-500">{atividade.funcionario?.cargo || "—"}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase font-semibold">Máquina / Veículo</p>
              <p className="text-base font-medium text-gray-800">{atividade.maquina?.veiculos || "—"}</p>
              <p className="text-xs text-gray-500">{atividade.maquina?.modelo || "—"}</p>
            </div>
          </div>
          {atividade.talhao && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase font-semibold">Talhão</p>
              <p className="text-base font-medium text-gray-800">{atividade.talhao?.nome || "—"}</p>
              {atividade.talhao?.area && (
                <p className="text-xs text-gray-500">Área: {atividade.talhao.area} hectares</p>
              )}
            </div>
          )}
          {atividade.insumos && atividade.insumos.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase font-semibold mb-2">Insumos Utilizados</p>
              <div className="space-y-1">
                {atividade.insumos.map((insumo, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{insumo.nome}</span>
                    <span>{insumo.quantidade} {insumo.unidade}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {atividade.observacoes && (
            <div className="bg-yellow-50 rounded-lg p-3 border-l-4 border-yellow-400">
              <p className="text-xs text-yellow-700 uppercase font-semibold">Observações</p>
              <p className="text-sm text-gray-700 mt-1">{atividade.observacoes}</p>
            </div>
          )}
          <div className="text-xs text-gray-400 pt-2 border-t">
            <p>ID: {atividade.id}</p>
            <p>Criado em: {atividade.created_at ? new Date(atividade.created_at).toLocaleString("pt-BR") : "—"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterBar({ filtros, setFiltros }) {
  const toggle = (key) => setFiltros((f) => ({ ...f, [key]: !f[key] }));
  const toggleGrao = (grao) =>
    setFiltros((f) => ({ ...f, graos: { ...f.graos, [grao]: !f.graos[grao] } }));
  const setAgroTipo = (val) => setFiltros((f) => ({ ...f, agroTipo: val }));

  const base = "flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-medium transition-colors cursor-pointer";
  const off = `${base} bg-gray-100 border-gray-200 text-gray-400`;
  const subOn = "px-2 py-0.5 rounded-full border text-xs font-medium cursor-pointer bg-green-50 border-green-300 text-green-700";
  const subOff = "px-2 py-0.5 rounded-full border text-xs cursor-pointer bg-gray-50 border-gray-200 text-gray-400 line-through";
  const radioOn = "px-2 py-0.5 rounded-full border text-xs font-medium cursor-pointer bg-orange-100 border-orange-300 text-orange-700";
  const radioOff = "px-2 py-0.5 rounded-full border text-xs cursor-pointer bg-gray-50 border-gray-200 text-gray-400";

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-2">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-500 font-medium">Exibir:</span>

        <button
          onClick={() => toggle("clima")}
          className={filtros.clima ? `${base} bg-blue-100 border-blue-300 text-blue-700` : off}
        >
          <TiWeatherPartlySunny size={13} /> Clima
        </button>

        <button
          onClick={() => toggle("graosAtivo")}
          className={filtros.graosAtivo ? `${base} bg-green-100 border-green-300 text-green-700` : off}
        >
          <PiGrainsLight size={13} /> Mercado de Grãos
        </button>

        {filtros.graosAtivo && (
          <>
            {[
              ["soja", "Soja"],
              ["milho", "Milho"],
              ["algodao", "Algodão"],
              ["arroz", "Arroz"],
            ].map(([key, label]) => (
              <button key={key} onClick={() => toggleGrao(key)} className={filtros.graos[key] ? subOn : subOff}>
                {label}
              </button>
            ))}
          </>
        )}

        <button
          onClick={() => toggle("agroAtivo")}
          className={filtros.agroAtivo ? `${base} bg-orange-100 border-orange-300 text-orange-700` : off}
        >
          <GiBull size={13} /> Dashboard Agro
        </button>

        {filtros.agroAtivo && (
          <>
            {[
              ["ambos", "Ambos"],
              ["macho", "Boi Adulto"],
              ["femea", "Vaca"],
            ].map(([val, label]) => (
              <button key={val} onClick={() => setAgroTipo(val)} className={filtros.agroTipo === val ? radioOn : radioOff}>
                {label}
              </button>
            ))}
          </>
        )}

        <button
          onClick={() => toggle("atividadesAtivo")}
          className={filtros.atividadesAtivo ? `${base} bg-yellow-100 border-yellow-300 text-yellow-700` : off}
        >
          <FaRegPaste size={13} /> Atividades
        </button>

        <button
          onClick={() => toggle("financeiro")}
          className={filtros.financeiro ? `${base} bg-purple-100 border-purple-300 text-purple-700` : off}
        >
          <GrMoney size={13} /> Financeiro
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [atividadeSelecionada, setAtividadeSelecionada] = useState(null);
  const { user: authUser, token } = useAuth();
  const user = authUser?.nome_completo || "Usuário";

  const [location, setLocation] = useState("—");
  const [weather, setWeather] = useState({
    temp: "—",
    max: "—",
    min: "—",
    humidity: "--",
    wind: "--",
    icon: "01d",
    description: "Carregando...",
  });

  const [animais, setAnimais] = useState(null);
  const [atividades, setAtividades] = useState(null);
  const [contasReceberVencidas, setContasReceberVencidas] = useState(null);
  const [contasPagarVencidas, setContasPagarVencidas] = useState(null);
  const [contasReceberPendentes, setContasReceberPendentes] = useState(null);
  const [contasPagarPendentes, setContasPagarPendentes] = useState(null);
  const [boiPreco, setBoiPreco] = useState(null);
  const [vacaPreco, setVacaPreco] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cotacoes, setCotacoes] = useState({
    soja: "—",
    milho: "—",
    algodao: "—",
    arroz: "—",
  });
  const [filtros, setFiltros] = useState({
    clima: true,
    graosAtivo: true,
    graos: { soja: true, milho: true, algodao: true, arroz: true },
    agroAtivo: true,
    agroTipo: "ambos",
    atividadesAtivo: true,
    financeiro: true,
  });

  useEffect(() => {
    if (!authUser) {
      navigate("/");
      return;
    }

    const loadData = async () => {
      if (!token) {
        setLoading(false);
        navigate("/");
        return;
      }

      try {
        setLoading(true);

        const [
          animaisData,
          atividadesData,
          receberData,
          pagarData,
          boiData,
          vacaData,
          cotacoesData,
          receberPendentesData,
          pagarPendentesData,
        ] = await Promise.all([
          fetchAnimais(token),
          fetchUltimasAtividades(token),
          fetchContasReceberVencidas(token),
          fetchContasPagarVencidas(token),
          fetchCattlePrice(token),
          fetchVacaPrice(token),
          fetchCotacoesGraos(token),
          fetchContasReceberPendentes(token),
          fetchContasPagarPendentes(token),
        ]);

        setAnimais(animaisData || []);
        setAtividades(atividadesData || []);
        setContasReceberVencidas(receberData || []);
        setContasPagarVencidas(pagarData || []);
        setBoiPreco(boiData);
        setVacaPreco(vacaData);
        setCotacoes(cotacoesData);
        setContasReceberPendentes(receberPendentesData || []);
        setContasPagarPendentes(pagarPendentesData || []);
      } catch (error) {
        console.error("Erro dashboard:", error);
        if (error.message === "Não autorizado") {
          localStorage.removeItem("token");
          navigate("/");
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [authUser, token, navigate]);

  useEffect(() => {
    if (!token) return;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => fetchWeather(pos.coords.latitude, pos.coords.longitude, token, setWeather, setLocation),
        () => fetchWeather(-22.1206, -51.3889, token, setWeather, setLocation)
      );
    }
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando dados do sistema...</p>
        </div>
      </div>
    );
  }

  if (
    animais === null ||
    atividades === null ||
    contasReceberVencidas === null ||
    contasPagarVencidas === null ||
    contasReceberPendentes === null ||
    contasPagarPendentes === null
  ) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto"></div>
          <p className="mt-4 text-gray-600">Aguardando dados...</p>
        </div>
      </div>
    );
  }

  // --- Cálculos financeiros ---
  const totalReceberVencido = contasReceberVencidas.reduce((sum, c) => sum + Number(c?.valor_final || 0), 0);
  const totalPagarVencido = contasPagarVencidas.reduce((sum, c) => sum + Number(c?.valor_final || 0), 0);
  const totalReceberPendente = contasReceberPendentes.reduce((sum, c) => sum + Number(c?.valor_final || 0), 0);
  const totalPagarPendente = contasPagarPendentes.reduce((sum, c) => sum + Number(c?.valor_final || 0), 0);

  // --- Categorias do rebanho bovino ---
  const animaisAtivosList = animais.filter((a) => a?.status === "A");
  const animaisVendidos = animais.filter((a) => a?.status === "V").length;
  const animaisMortos = animais.filter((a) => a?.status === "M").length;

  const boiAdultos = animaisAtivosList.filter((a) => calcularCategoria(a) === "boi_adulto");
  const vacasAtivas = animaisAtivosList.filter((a) => calcularCategoria(a) === "vaca");

  const pesoBoiAdultosArrobas = boiAdultos
    .reduce((sum, a) => sum + (Number(a?.peso) || 0) / 15, 0)
    .toFixed(1);

  const pesoVacasArrobas = vacasAtivas
    .reduce((sum, a) => sum + (Number(a?.peso) || 0) / 15, 0)
    .toFixed(1);

  const mostrarBoiAdulto =
    filtros.agroAtivo && (filtros.agroTipo === "ambos" || filtros.agroTipo === "macho");
  const mostrarVaca =
    filtros.agroAtivo && (filtros.agroTipo === "ambos" || filtros.agroTipo === "femea");

  const doughnutData = {
    labels: ["Receber Vencido", "Receber Pendente", "Pagar Vencido", "Pagar Pendente"],
    datasets: [
      {
        data: [totalReceberVencido, totalReceberPendente, totalPagarVencido, totalPagarPendente],
        backgroundColor: ["#ef4444", "#22c55e", "#f97316", "#eab308"],
        borderColor: ["#fff", "#fff", "#fff", "#fff"],
        borderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { padding: 12, font: { size: 11 } } },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            ` R$ ${Number(ctx.raw).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        },
      },
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              {saudacaoPorHorario()}, {user} <MdOutlineWavingHand size={30} />
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Hoje é: {new Date().toLocaleDateString("pt-BR")}
            </p>
          </div>
        </header>

        {/* Clima */}
        {filtros.clima && (
          <div className="max-w-7xl mx-auto mt-4">
            <div className="bg-blue-50 rounded-xl shadow-sm p-4 border-l-4 border-blue-500 flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 uppercase tracking-wide font-semibold flex items-center gap-2">
                  <TiWeatherPartlySunny size={38} /> Clima Atual
                </p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{weather.temp}</p>
                <p className="text-sm text-gray-500">{location}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Máx {weather.max} • Min {weather.min}
                </p>
              </div>
              <img
                src={`https://openweathermap.org/img/wn/${weather.icon}@2x.png`}
                alt="clima"
                className="w-16 h-16"
              />
            </div>
          </div>
        )}
      </div>

      {/* Barra de filtros */}
      <FilterBar filtros={filtros} setFiltros={setFiltros} />

      <main className="max-w-7xl mx-auto px-6 py-6">

        {/* Mercado de Grãos */}
        {filtros.graosAtivo && (
          <div className="max-w-7xl mx-auto mt-4 mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <PiPlantLight size={30} /> Mercado de Grãos
              <span className="text-sm font-normal text-gray-500">Fonte: Notícias Agrícolas</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {filtros.graos.soja && (
                <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-200">
                  <a
                    href="https://www.noticiasagricolas.com.br/cotacoes/soja"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-600 uppercase tracking-wide flex items-center gap-2 hover:text-green-600 transition-colors"
                  >
                    <PiGrainsLight size={20} /> <span>Soja</span>
                  </a>
                  <p className="text-2xl font-bold text-gray-800 mt-1">
                    R${" "}
                    {cotacoes?.soja?.media
                      ? Number(cotacoes.soja.media).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "—"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Média das praças (SC 60kg)</p>
                  {cotacoes?.soja?.cidades?.length > 0 && (
                    <div className="mt-3 border-t pt-2 text-xs text-gray-600 space-y-1">
                      <p className="flex items-center gap-2">
                        <BsGraphUpArrow size={12} /> Maior: R$ {cotacoes.soja.maior_preco} em{" "}
                        {cotacoes.soja.cidade_maior_preco}
                      </p>
                      <p className="flex items-center gap-2">
                        <BsGraphDownArrow size={12} /> Menor: R$ {cotacoes.soja.menor_preco} em{" "}
                        {cotacoes.soja.cidade_menor_preco}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {filtros.graos.milho && (
                <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-yellow-300">
                  <a
                    href="https://www.noticiasagricolas.com.br/cotacoes/milho"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-600 uppercase tracking-wide flex items-center gap-2 hover:text-yellow-400 transition-opacity"
                  >
                    <GiCorn size={20} /> <span>Milho</span>
                  </a>
                  <p className="text-2xl font-bold text-gray-800 mt-1">
                    R${" "}
                    {cotacoes?.milho?.media
                      ? Number(cotacoes.milho.media).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "—"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Média das praças (SC 60kg)</p>
                  {cotacoes?.milho?.cidades?.length > 0 && (
                    <div className="mt-3 border-t pt-2 text-xs text-gray-600 space-y-1">
                      <p className="flex items-center gap-2">
                        <BsGraphUpArrow size={12} /> Maior: R$ {cotacoes.milho.maior_preco} em{" "}
                        {cotacoes.milho.cidade_maior_preco}
                      </p>
                      <p className="flex items-center gap-2">
                        <BsGraphDownArrow size={12} /> Menor: R$ {cotacoes.milho.menor_preco} em{" "}
                        {cotacoes.milho.cidade_menor_preco}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {filtros.graos.algodao && (
                <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-500">
                  <a
                    href="https://www.noticiasagricolas.com.br/cotacoes/algodao"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-600 uppercase tracking-wide flex items-center gap-2 hover:text-blue-600 transition-colors"
                  >
                    <GiCottonFlower size={20} /> <span>Algodão</span>
                  </a>
                  <p className="text-2xl font-bold text-gray-800 mt-1">
                    R${" "}
                    {cotacoes?.algodao?.media
                      ? Number(cotacoes.algodao.media).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "—"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Média das praças (arroba)</p>
                  {cotacoes?.algodao?.cidades?.length > 0 && (
                    <div className="mt-3 border-t pt-2 text-xs text-gray-600 space-y-1">
                      <p className="flex items-center gap-2">
                        <BsGraphUpArrow size={12} /> Maior: R$ {cotacoes.algodao.maior_preco} em{" "}
                        {cotacoes.algodao.cidade_maior_preco}
                      </p>
                      <p className="flex items-center gap-2">
                        <BsGraphDownArrow size={12} /> Menor: R$ {cotacoes.algodao.menor_preco} em{" "}
                        {cotacoes.algodao.cidade_menor_preco}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {filtros.graos.arroz && (
                <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-gray-500">
                  <a
                    href="https://www.noticiasagricolas.com.br/cotacoes/arroz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-600 uppercase tracking-wide flex items-center gap-2 hover:text-gray-700 transition-colors"
                  >
                    <FaBowlRice size={20} /> <span>Arroz</span>
                  </a>
                  <p className="text-2xl font-bold text-gray-800 mt-1">
                    R${" "}
                    {cotacoes?.arroz?.media
                      ? Number(cotacoes.arroz.media).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : "—"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Média das praças (saca 50kg)</p>
                  {cotacoes?.arroz?.cidades?.length > 0 && (
                    <div className="mt-3 border-t pt-2 text-xs text-gray-600 space-y-1">
                      <p className="flex items-center gap-2">
                        <BsGraphUpArrow size={12} /> Maior: R$ {cotacoes.arroz.maior_preco} em{" "}
                        {cotacoes.arroz.cidade_maior_preco}
                      </p>
                      <p className="flex items-center gap-2">
                        <BsGraphDownArrow size={12} /> Menor: R$ {cotacoes.arroz.menor_preco} em{" "}
                        {cotacoes.arroz.cidade_menor_preco}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dashboard Agro */}
        {filtros.agroAtivo && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <VscGraph size={30} /> Dashboard Agro
              <span className="text-sm font-normal text-gray-500">Gestão do rebanho</span>
            </h2>

            {/* Cards: Boi Adulto */}
            {mostrarBoiAdulto && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                  <GiBull size={16} className="text-orange-600" /> Boi Adulto (bovino macho ≥ 3 anos)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-600">
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-2">
                      <PiCowLight size={20} /> Total de Bovinos (Boi Adulto)
                    </p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">{boiAdultos.length}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {animaisAtivosList.length} bovinos ativos • {animaisVendidos} vendidos • {animaisMortos} mortos
                    </p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-blue-600">
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-2">
                      <MdBalance size={20} /> Peso Total (Arrobas)
                    </p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">{pesoBoiAdultosArrobas} @</p>
                    <p className="text-xs text-gray-500 mt-1">Apenas boi adulto ativo</p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-purple-600">
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-2">
                      <IoPricetagsOutline size={20} /> Valor Estimado do Rebanho
                    </p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      R${" "}
                      {(parseFloat(pesoBoiAdultosArrobas) * Number(boiPreco?.price || 0)).toLocaleString(
                        "pt-BR",
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Baseado no @ do boi gordo</p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-orange-600">
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-2">
                      <GiBull size={20} /> Preço do Boi Gordo
                    </p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      R$ {Number(boiPreco?.price || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Mercado internacional</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Atualizado em:{" "}
                      {boiPreco?.data ? boiPreco.data.split("-").reverse().join("/") : "—"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Cards: Vaca */}
            {mostrarVaca && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                  <PiCowLight size={16} className="text-pink-600" /> Vaca (bovino fêmea ≥ 3 anos)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-pink-500">
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-2">
                      <PiCowLight size={20} /> Total de Bovinos (Vacas)
                    </p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">{vacasAtivas.length}</p>
                    <p className="text-xs text-gray-500 mt-1">Vacas ativas na fazenda</p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-teal-600">
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-2">
                      <MdBalance size={20} /> Peso Total (Arrobas)
                    </p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">{pesoVacasArrobas} @</p>
                    <p className="text-xs text-gray-500 mt-1">Apenas vacas ativas</p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-indigo-600">
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-2">
                      <IoPricetagsOutline size={20} /> Valor Estimado do Rebanho
                    </p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      R${" "}
                      {(parseFloat(pesoVacasArrobas) * Number(vacaPreco?.price || 0)).toLocaleString(
                        "pt-BR",
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Baseado no @ da vaca gorda</p>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-rose-500">
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-2">
                      <PiCowLight size={20} /> Preço da Vaca Gorda
                    </p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">
                      R$ {Number(vacaPreco?.price || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Mercado — noticiasagricolas</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Atualizado em:{" "}
                      {vacaPreco?.data ? vacaPreco.data.split("-").reverse().join("/") : "—"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Últimas atividades */}
            {filtros.atividadesAtivo && <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">Últimas Atividades Realizadas</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {atividades.length > 0 ? (
                  atividades.slice(0, 6).map((atividade, idx) => (
                    <div
                      key={idx}
                      onClick={() => setAtividadeSelecionada(atividade)}
                      className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-green-500 hover:shadow-md transition-all cursor-pointer hover:scale-[1.02]"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <FaRegPaste size={18} className="text-green-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800 text-sm">
                              {atividade.tipo_atividade?.tipo_principal || "Atividade"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {atividade.tipo_atividade?.nome || "Registro"}
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                          {atividade?.data ? atividade.data.split("-").reverse().join("/") : "—"}
                        </span>
                      </div>
                      <div className="mt-3 space-y-1">
                        <p className="text-xs text-gray-600 flex items-center gap-2">
                          <IoMdPerson size={18} />
                          <span className="font-medium">Responsável:</span>{" "}
                          {atividade.funcionario?.nome || "—"}
                        </p>
                        <p className="text-xs text-gray-600 flex items-center gap-2">
                          <GiFarmTractor size={18} />
                          <span className="font-medium">Máquina:</span>{" "}
                          {atividade.maquina?.veiculos || "—"}
                        </p>
                        {atividade.talhao && (
                          <p className="text-xs text-gray-600 flex items-center gap-2">
                            <GiFallingLeaf size={18} />
                            <span className="font-medium">Talhão:</span>{" "}
                            {atividade.talhao?.nome || "—"}
                          </p>
                        )}
                      </div>
                      <div className="mt-3 pt-2 border-t border-gray-100 flex items-center gap-2">
                        <p className="text-xs text-green-600 flex items-center gap-1">
                          <FaMagnifyingGlass size={18} /> Clique para ver detalhes
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full bg-gray-50 rounded-xl p-8 text-center text-gray-400">
                    Nenhuma atividade registrada
                  </div>
                )}
              </div>
            </div>}
          </div>
        )}

        {/* Dashboard Financeiro */}
        {filtros.financeiro && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <GrMoney size={30} /> Dashboard Financeiro
              <span className="text-sm font-normal text-gray-500">Controle de fluxo de caixa</span>
            </h2>

            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 w-full max-w-md">
                <p className="text-sm font-semibold text-gray-700 mb-1">Balanço Financeiro</p>
                <p className="text-xs text-gray-400 mb-3">Distribuição de valores (R$)</p>
                <div className="h-52">
                  {totalReceberVencido + totalReceberPendente + totalPagarVencido + totalPagarPendente > 0 ? (
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-300 text-sm">
                      Sem dados financeiros
                    </div>
                  )}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="bg-red-50 rounded-lg px-3 py-2 text-center">
                    <p className="text-xs text-red-500 font-medium">A Receber Vencido</p>
                    <p className="text-sm font-bold text-red-700">
                      R${" "}
                      {totalReceberVencido.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg px-3 py-2 text-center">
                    <p className="text-xs text-green-600 font-medium">A Receber Pendente</p>
                    <p className="text-sm font-bold text-green-700">
                      R${" "}
                      {totalReceberPendente.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg px-3 py-2 text-center">
                    <p className="text-xs text-orange-500 font-medium">A Pagar Vencido</p>
                    <p className="text-sm font-bold text-orange-700">
                      R${" "}
                      {totalPagarVencido.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="bg-yellow-50 rounded-lg px-3 py-2 text-center">
                    <p className="text-xs text-yellow-600 font-medium">A Pagar Pendente</p>
                    <p className="text-sm font-bold text-yellow-700">
                      R${" "}
                      {totalPagarPendente.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-red-50 rounded-xl shadow-sm p-4 border-l-4 border-red-500">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-red-600 uppercase tracking-wide font-semibold flex items-center gap-2">
                      <GoAlertFill size={23} /> Contas a Receber Vencidas
                    </p>
                    <p className="text-2xl font-bold text-red-700 mt-1">
                      R$ {totalReceberVencido.toLocaleString("pt-BR")}
                    </p>
                  </div>
                  <span className="bg-red-200 text-red-800 text-xs font-bold px-2 py-1 rounded-full">
                    {contasReceberVencidas.length} pendente(s)
                  </span>
                </div>
                {contasReceberVencidas.length > 0 && (
                  <div className="mt-3 text-sm">
                    <p className="font-medium text-red-700">Últimos vencimentos:</p>
                    <ul className="mt-1 space-y-1">
                      {contasReceberVencidas.slice(0, 3).map((conta, idx) => (
                        <li key={idx} className="text-xs text-red-600 flex justify-between">
                          <span>{conta.cliente_nome}</span>
                          <span>R$ {(conta.valor_final || 0).toLocaleString("pt-BR")}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="bg-yellow-50 rounded-xl shadow-sm p-4 border-l-4 border-yellow-500">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-yellow-700 uppercase tracking-wide font-semibold flex items-center gap-2">
                      <GoAlertFill size={23} /> Contas a Pagar Vencidas
                    </p>
                    <p className="text-2xl font-bold text-yellow-700 mt-1">
                      R$ {totalPagarVencido.toLocaleString("pt-BR")}
                    </p>
                  </div>
                  <span className="bg-yellow-200 text-yellow-800 text-xs font-bold px-2 py-1 rounded-full">
                    {contasPagarVencidas.length} pendente(s)
                  </span>
                </div>
                {contasPagarVencidas.length > 0 && (
                  <div className="mt-3 text-sm">
                    <p className="font-medium text-yellow-700">Últimos vencimentos:</p>
                    <ul className="mt-1 space-y-1">
                      {contasPagarVencidas.slice(0, 3).map((conta, idx) => (
                        <li key={idx} className="text-xs text-yellow-600 flex justify-between">
                          <span>{conta.fornecedor_nome}</span>
                          <span>R$ {(conta.valor_final || 0).toLocaleString("pt-BR")}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {atividadeSelecionada && (
        <AtividadeModal
          atividade={atividadeSelecionada}
          onClose={() => setAtividadeSelecionada(null)}
        />
      )}

      <footer className="text-center py-6 mt-6 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Novalis Agro • Dados em tempo real do sistema © 2026
        </p>
      </footer>
    </div>
  );
}
